import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../ui/button';
import { ToggleGroup, ToggleGroupItem } from '../../ui/toggle-group';
import {
    isCurrentPeriod,
    periodToLabel,
    shiftPeriod,
    withPeriodUnit,
    type PeriodState,
    type PeriodUnit,
} from './periodUtils';

interface PeriodPickerProps {
    period: PeriodState;
    onChange: (period: PeriodState) => void;
}

const UNIT_VALUES: PeriodUnit[] = ['week', 'month', 'quarter'];

const isPeriodUnit = (value: string): value is PeriodUnit =>
    (UNIT_VALUES as string[]).includes(value);

export default function PeriodPicker({ period, onChange }: PeriodPickerProps) {
    const { t } = useTranslation();

    const handleUnitChange = (next: string) => {
        if (isPeriodUnit(next)) {
            onChange(withPeriodUnit(next));
        }
    };

    return (
        <div className="flex items-center gap-2">
            <ToggleGroup
                type="single"
                value={period.unit}
                onValueChange={handleUnitChange}
                variant="outline"
                className="h-7 gap-0 rounded-md p-0"
                aria-label={t('facilities.subscription.choosePeriod')}
            >
                <ToggleGroupItem
                    value="week"
                    className="h-full rounded-none border-0 px-2.5 text-xs first:rounded-l-md"
                    aria-label={t('facilities.subscription.weeklyView')}
                >
                    1W
                </ToggleGroupItem>
                <ToggleGroupItem
                    value="month"
                    className="h-full rounded-none border-0 border-x border-input px-2.5 text-xs"
                    aria-label={t('facilities.subscription.monthlyView')}
                >
                    1M
                </ToggleGroupItem>
                <ToggleGroupItem
                    value="quarter"
                    className="h-full rounded-none border-0 px-2.5 text-xs last:rounded-r-md"
                    aria-label={t('facilities.subscription.quarterlyView')}
                >
                    1Q
                </ToggleGroupItem>
            </ToggleGroup>

            <div className="inline-flex h-7 items-center rounded-md border border-input bg-background">
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-full w-7 rounded-none rounded-l-md"
                    onClick={() => onChange(shiftPeriod(period, -1))}
                    aria-label={t('facilities.subscription.previousPeriod')}
                >
                    <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
                </Button>
                <span className="min-w-28 px-1 text-center text-xs font-medium tabular-nums">
                    {periodToLabel(period)}
                </span>
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-full w-7 rounded-none rounded-r-md"
                    disabled={isCurrentPeriod(period)}
                    onClick={() => onChange(shiftPeriod(period, 1))}
                    aria-label={t('facilities.subscription.nextPeriod')}
                >
                    <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                </Button>
            </div>
        </div>
    );
}
